import type { CSSProperties } from 'react';

import type { DeviceLightCapabilityValues } from '../../lib/capabilities/deviceLightCapabilities';
import {
  getColorForHueSaturation,
  getColorForTemperature,
  getDefaultColor,
  getMode,
} from '../../lib/capabilities/light';

import { theme } from '../../theme/theme';

const DEFAULT_QUICK_ACTION_BACKGROUND_COLOR = theme.device.tile_quick_action_off_fill;
const DEFAULT_TILE_OFF_QUICK_ACTION_BACKGROUND_COLOR = theme.device.tile_quick_action_off_fill_tile;
const DEFAULT_QUICK_ACTION_ICON_COLOR = theme.device.tile_quick_action_off_glyph;
const DEFAULT_QUICK_ACTION_HOVER_BACKGROUND_COLOR =
  theme.device.tile_quick_action_off_press_in_fill;
const DEFAULT_QUICK_ACTION_HOVER_ICON_COLOR = theme.device.tile_quick_action_off_press_in_glyph;
const DEFAULT_SELECTED_QUICK_ACTION_BACKGROUND_COLOR = theme.device.tile_quick_action_on_off_fill;
const DEFAULT_SELECTED_QUICK_ACTION_ICON_COLOR = theme.device.tile_quick_action_on_off_glyph;
const DEFAULT_SELECTED_QUICK_ACTION_HOVER_BACKGROUND_COLOR =
  theme.device.tile_quick_action_on_off_press_in_fill;
const DEFAULT_SELECTED_QUICK_ACTION_HOVER_ICON_COLOR =
  theme.device.tile_quick_action_on_off_press_in_glyph;
const DEFAULT_QUICK_ACTION_ICON_SIZE = '20px';
const MEDIA_SELECTED_QUICK_ACTION_BACKGROUND_COLOR = theme.device.tile_quick_action_speaker_fill;
const MEDIA_SELECTED_QUICK_ACTION_ICON_COLOR = theme.device.tile_quick_action_speaker_glyph;
const MEDIA_SELECTED_QUICK_ACTION_HOVER_BACKGROUND_COLOR =
  theme.device.tile_quick_action_speaker_press_in_fill;
const MEDIA_SELECTED_QUICK_ACTION_HOVER_ICON_COLOR =
  theme.device.tile_quick_action_speaker_press_in_glyph;
const LIGHT_SELECTED_QUICK_ACTION_OVERLAY_COLOR = 'transparent';
const WHITE_LIGHT_QUICK_ACTION_BACKGROUND_COLOR = theme.color.white;
const WHITE_LIGHT_QUICK_ACTION_BORDER_COLOR = theme.device.tile_quick_action_off_fill;
const WHITE_LIGHT_QUICK_ACTION_HOVER_BACKGROUND_COLOR = theme.device.tile_quick_action_off_fill;
const LUMINANCE_RED = 0.2126;
const LUMINANCE_GREEN = 0.7152;
const LUMINANCE_BLUE = 0.0722;
const SRGB_LINEAR_THRESHOLD = 0.04045;
const SRGB_LINEAR_SCALE = 12.92;
const SRGB_GAMMA = 2.4;
const SRGB_INVERSE_THRESHOLD = 0.0031308;
const LIGHT_QUICK_ACTION_FADED_OPACITY = 0.2;
const LIGHT_MODE = {
  clamp: { above: 0.9, target: 0.7 },
  iconFill: {
    saturation: { min: 0.5, max: 1 },
    chroma: { target: 0.18 },
    opacity: { min: 0.05, max: 0.5 },
  },
  iconFillPressIn: {
    luminanceRatio: 0.85,
  },
  iconGlyph: {
    chroma: { min: 0.3, max: 0.7 },
    luminance: { min: 0.3, max: 0.4 },
  },
  iconGlyphPressIn: {
    luminanceRatio: 0.5,
  },
};
const DARK_MODE = {
  clamp: { below: 0.2, target: 0.25 },
  iconFillPressIn: {
    lightnessDelta: 0.12,
    opacityMultiplier: 1.6,
  },
  iconGlyphPressIn: {
    lightnessDelta: 0.12,
  },
};
const SHARED_MODE = {
  pressInSaturationBoost: 1.3,
};

interface RGBColor {
  red: number;
  green: number;
  blue: number;
}

interface RGBColorAdjustment extends RGBColor {
  wasAdjusted: boolean;
}

interface LightColors {
  color: string;
  fadedColor: string;
  pressInColor: string;
  pressInFadedColor: string;
}

export type DeviceQuickActionLightValues = DeviceLightCapabilityValues;

export type DeviceQuickActionColors = {
  backgroundColor: string;
  borderColor: string;
  iconSize: string;
  iconColor: string;
  outlineColor: string;
  overlayColor: string;
};

export type DeviceQuickActionStyle = CSSProperties & {
  '--prop-background-color': string;
  '--prop-border-color': string;
  '--prop-hover-background-color': string;
  '--prop-hover-border-color': string;
  '--prop-icon-color': string;
  '--prop-icon-size': string;
  '--prop-hover-icon-color': string;
  '--prop-outline-color': string;
  '--prop-overlay-color': string;
  '--prop-pulse-color'?: string;
  '--prop-hover-overlay-color': string;
};

export function getDefaultDeviceQuickActionColors(props: {
  isActive: boolean;
  isHovered: boolean;
  isTileOff?: boolean;
}): DeviceQuickActionColors {
  if (props.isActive !== true) {
    return getInactiveDeviceQuickActionColors({
      isHovered: props.isHovered,
      isTileOff: props.isTileOff,
    });
  }

  return {
    backgroundColor:
      props.isHovered === true
        ? DEFAULT_SELECTED_QUICK_ACTION_HOVER_BACKGROUND_COLOR
        : DEFAULT_SELECTED_QUICK_ACTION_BACKGROUND_COLOR,
    borderColor: 'transparent',
    iconSize: DEFAULT_QUICK_ACTION_ICON_SIZE,
    iconColor:
      props.isHovered === true
        ? DEFAULT_SELECTED_QUICK_ACTION_HOVER_ICON_COLOR
        : DEFAULT_SELECTED_QUICK_ACTION_ICON_COLOR,
    outlineColor: DEFAULT_SELECTED_QUICK_ACTION_ICON_COLOR,
    overlayColor: 'transparent',
  };
}

export function getLightDeviceQuickActionColors(props: {
  isActive: boolean;
  isHovered: boolean;
  isLightTheme: boolean;
  isTileOff?: boolean;
  lightValues: DeviceQuickActionLightValues;
}): DeviceQuickActionColors {
  if (props.isActive !== true) {
    return getInactiveDeviceQuickActionColors({
      isHovered: props.isHovered,
      isTileOff: props.isTileOff,
    });
  }

  const baseLightColor = getSelectedLightColor(props.lightValues);
  const rgb = baseLightColor.toRgb();
  const lightColors = getModeAdjustedColors({
    blue: rgb.b,
    fadedOpacity: LIGHT_QUICK_ACTION_FADED_OPACITY,
    green: rgb.g,
    isLightTheme: props.isLightTheme,
    red: rgb.r,
  });
  const isWhite = rgb.r === 255 && rgb.g === 255 && rgb.b === 255;

  if (isWhite === true && props.isLightTheme === true) {
    return {
      backgroundColor:
        props.isHovered === true
          ? WHITE_LIGHT_QUICK_ACTION_HOVER_BACKGROUND_COLOR
          : WHITE_LIGHT_QUICK_ACTION_BACKGROUND_COLOR,
      borderColor: WHITE_LIGHT_QUICK_ACTION_BORDER_COLOR,
      iconSize: DEFAULT_QUICK_ACTION_ICON_SIZE,
      iconColor: props.isHovered === true ? lightColors.pressInColor : lightColors.color,
      outlineColor: lightColors.color,
      overlayColor: LIGHT_SELECTED_QUICK_ACTION_OVERLAY_COLOR,
    };
  }

  return {
    backgroundColor:
      props.isHovered === true ? lightColors.pressInFadedColor : lightColors.fadedColor,
    borderColor: 'transparent',
    iconSize: DEFAULT_QUICK_ACTION_ICON_SIZE,
    iconColor: props.isHovered === true ? lightColors.pressInColor : lightColors.color,
    outlineColor: lightColors.color,
    overlayColor: LIGHT_SELECTED_QUICK_ACTION_OVERLAY_COLOR,
  };
}

export function getMediaDeviceQuickActionColors(props: {
  isActive: boolean;
  isHovered: boolean;
  isTileOff?: boolean;
}): DeviceQuickActionColors {
  if (props.isActive !== true) {
    return getInactiveDeviceQuickActionColors({
      isHovered: props.isHovered,
      isTileOff: props.isTileOff,
    });
  }

  return {
    backgroundColor:
      props.isHovered === true
        ? MEDIA_SELECTED_QUICK_ACTION_HOVER_BACKGROUND_COLOR
        : MEDIA_SELECTED_QUICK_ACTION_BACKGROUND_COLOR,
    borderColor: 'transparent',
    iconSize: DEFAULT_QUICK_ACTION_ICON_SIZE,
    iconColor:
      props.isHovered === true
        ? MEDIA_SELECTED_QUICK_ACTION_HOVER_ICON_COLOR
        : MEDIA_SELECTED_QUICK_ACTION_ICON_COLOR,
    outlineColor: MEDIA_SELECTED_QUICK_ACTION_ICON_COLOR,
    overlayColor: 'transparent',
  };
}

export function getModeAdjustedRGB(props: {
  red: number;
  green: number;
  blue: number;
  isLightTheme: boolean;
}): RGBColorAdjustment {
  const luminance = getPerceivedLuminance(props.red, props.green, props.blue);

  if (props.isLightTheme === true && luminance > LIGHT_MODE.clamp.above) {
    const adjustedColor = adjustLuminanceTo({
      blue: props.blue,
      green: props.green,
      red: props.red,
      targetLuminance: LIGHT_MODE.clamp.target,
    });

    return { ...adjustedColor, wasAdjusted: true };
  }

  if (props.isLightTheme !== true && luminance < DARK_MODE.clamp.below) {
    const adjustedColor = mixToTargetLuminance({
      blue: props.blue,
      green: props.green,
      red: props.red,
      targetLuminance: DARK_MODE.clamp.target,
    });

    return { ...adjustedColor, wasAdjusted: true };
  }

  return { blue: props.blue, green: props.green, red: props.red, wasAdjusted: false };
}

function getInactiveDeviceQuickActionColors(props: {
  isHovered: boolean;
  isTileOff?: boolean;
}): DeviceQuickActionColors {
  return {
    backgroundColor:
      props.isHovered === true
        ? DEFAULT_QUICK_ACTION_HOVER_BACKGROUND_COLOR
        : props.isTileOff === true
          ? DEFAULT_TILE_OFF_QUICK_ACTION_BACKGROUND_COLOR
          : DEFAULT_QUICK_ACTION_BACKGROUND_COLOR,
    borderColor: 'transparent',
    iconSize: DEFAULT_QUICK_ACTION_ICON_SIZE,
    iconColor:
      props.isHovered === true
        ? DEFAULT_QUICK_ACTION_HOVER_ICON_COLOR
        : DEFAULT_QUICK_ACTION_ICON_COLOR,
    outlineColor: DEFAULT_QUICK_ACTION_ICON_COLOR,
    overlayColor: 'transparent',
  };
}

function getSelectedLightColor(lightValues: DeviceQuickActionLightValues) {
  const lightMode = getMode({
    light_hue: lightValues.light_hue,
    light_mode: lightValues.light_mode,
    light_saturation: lightValues.light_saturation,
    light_temperature: lightValues.light_temperature,
  });

  if (
    lightValues.light_temperature == null &&
    lightValues.light_hue == null &&
    lightValues.light_saturation == null
  ) {
    return getDefaultColor();
  }

  if (lightMode === 'color') {
    return getColorForHueSaturation({
      light_hue: lightValues.light_hue,
      light_saturation: lightValues.light_saturation,
    });
  }

  return getColorForTemperature({
    light_temperature: lightValues.light_temperature,
  });
}

function getModeAdjustedColors(props: {
  red: number;
  green: number;
  blue: number;
  isLightTheme: boolean;
  fadedOpacity: number;
}): LightColors {
  const adjusted = getModeAdjustedRGB({
    blue: props.blue,
    green: props.green,
    isLightTheme: props.isLightTheme,
    red: props.red,
  });

  if (props.isLightTheme === true) {
    return computeLightModeColors(adjusted);
  }

  return computeDarkModeColors(adjusted, props.fadedOpacity);
}

function computeLightModeColors(adjustedColor: RGBColor): LightColors {
  const fillBase = liftHslSaturationRange({
    blue: adjustedColor.blue,
    green: adjustedColor.green,
    rangeEnd: LIGHT_MODE.iconFill.saturation.max,
    rangeStart: LIGHT_MODE.iconFill.saturation.min,
    red: adjustedColor.red,
  });
  const fillOpacity = solveFillOpacity({
    blue: fillBase.blue,
    green: fillBase.green,
    maxOpacity: LIGHT_MODE.iconFill.opacity.max,
    minOpacity: LIGHT_MODE.iconFill.opacity.min,
    red: fillBase.red,
    targetChroma: LIGHT_MODE.iconFill.chroma.target,
  });
  const iconBase = equalizeChromaAndLuminanceHsv({
    blue: adjustedColor.blue,
    chromaMax: LIGHT_MODE.iconGlyph.chroma.max,
    chromaMin: LIGHT_MODE.iconGlyph.chroma.min,
    green: adjustedColor.green,
    luminanceMax: LIGHT_MODE.iconGlyph.luminance.max,
    luminanceMin: LIGHT_MODE.iconGlyph.luminance.min,
    red: adjustedColor.red,
  });
  const iconPressIn = equalizeChromaAndLuminanceHsv({
    blue: adjustedColor.blue,
    chromaMax: LIGHT_MODE.iconGlyph.chroma.max,
    chromaMin: LIGHT_MODE.iconGlyph.chroma.min,
    green: adjustedColor.green,
    luminanceMax: LIGHT_MODE.iconGlyph.luminance.max * LIGHT_MODE.iconGlyphPressIn.luminanceRatio,
    luminanceMin: LIGHT_MODE.iconGlyph.luminance.min * LIGHT_MODE.iconGlyphPressIn.luminanceRatio,
    red: adjustedColor.red,
  });
  const visibleFill = blendOverWhite({
    blue: fillBase.blue,
    green: fillBase.green,
    opacity: fillOpacity,
    red: fillBase.red,
  });
  const pressInFill = darkenByLuminanceRatio({
    blue: visibleFill.blue,
    green: visibleFill.green,
    ratio: LIGHT_MODE.iconFillPressIn.luminanceRatio,
    red: visibleFill.red,
  });

  return {
    color: getRGBAFromRGB(iconBase.red, iconBase.green, iconBase.blue, 1),
    fadedColor: getRGBAFromRGB(fillBase.red, fillBase.green, fillBase.blue, fillOpacity),
    pressInColor: getRGBAFromRGB(iconPressIn.red, iconPressIn.green, iconPressIn.blue, 1),
    pressInFadedColor: getRGBAFromRGB(pressInFill.red, pressInFill.green, pressInFill.blue, 1),
  };
}

function computeDarkModeColors(adjustedColor: RGBColor, fadedOpacity: number): LightColors {
  const pressInFill = applyContrastTransform({
    blue: adjustedColor.blue,
    green: adjustedColor.green,
    lightnessDelta: DARK_MODE.iconFillPressIn.lightnessDelta,
    red: adjustedColor.red,
  });
  const pressInFillOpacity = Math.min(
    1,
    fadedOpacity * DARK_MODE.iconFillPressIn.opacityMultiplier,
  );
  const iconPressIn = applyContrastTransform({
    blue: adjustedColor.blue,
    green: adjustedColor.green,
    lightnessDelta: DARK_MODE.iconGlyphPressIn.lightnessDelta,
    red: adjustedColor.red,
  });

  return {
    color: getRGBAFromRGB(adjustedColor.red, adjustedColor.green, adjustedColor.blue, 1),
    fadedColor: getRGBAFromRGB(
      adjustedColor.red,
      adjustedColor.green,
      adjustedColor.blue,
      fadedOpacity,
    ),
    pressInColor: getRGBAFromRGB(iconPressIn.red, iconPressIn.green, iconPressIn.blue, 1),
    pressInFadedColor: getRGBAFromRGB(
      pressInFill.red,
      pressInFill.green,
      pressInFill.blue,
      pressInFillOpacity,
    ),
  };
}

function liftHslSaturationRange(
  props: RGBColor & { rangeStart: number; rangeEnd: number },
): RGBColor {
  const hsl = rgbToHsl(props.red, props.green, props.blue);

  if (hsl.saturation === 0) {
    return { blue: props.blue, green: props.green, red: props.red };
  }

  const liftedSaturation = Math.min(
    1,
    props.rangeStart + hsl.saturation * (props.rangeEnd - props.rangeStart),
  );

  return hslToRgb(hsl.hue, liftedSaturation, hsl.lightness);
}

function equalizeChromaAndLuminanceHsv(
  props: RGBColor & {
    chromaMin: number;
    chromaMax: number;
    luminanceMin: number;
    luminanceMax: number;
  },
): RGBColor {
  const hsv = rgbToHsv(props.red, props.green, props.blue);
  const targetChroma = props.chromaMin + hsv.saturation * (props.chromaMax - props.chromaMin);
  const targetLuminance =
    props.luminanceMin + hsv.saturation * (props.luminanceMax - props.luminanceMin);

  if (hsv.saturation === 0) {
    const grayValue = linearToSrgb(targetLuminance);

    return { blue: grayValue, green: grayValue, red: grayValue };
  }

  const maxLuminancePoint = hsvToRgb(hsv.hue, targetChroma, 1);
  const maxLuminance = getPerceivedLuminance(
    maxLuminancePoint.red,
    maxLuminancePoint.green,
    maxLuminancePoint.blue,
  );

  if (targetLuminance >= maxLuminance) {
    return maxLuminancePoint;
  }

  const minLuminancePoint = hsvToRgb(hsv.hue, 1, targetChroma);
  const minLuminance = getPerceivedLuminance(
    minLuminancePoint.red,
    minLuminancePoint.green,
    minLuminancePoint.blue,
  );

  if (targetLuminance <= minLuminance) {
    return minLuminancePoint;
  }

  let lowSaturation = targetChroma;
  let highSaturation = 1;

  for (let index = 0; index < 12; index++) {
    const midSaturation = (lowSaturation + highSaturation) / 2;
    const color = hsvToRgb(hsv.hue, midSaturation, targetChroma / midSaturation);

    if (getPerceivedLuminance(color.red, color.green, color.blue) > targetLuminance) {
      lowSaturation = midSaturation;
    } else {
      highSaturation = midSaturation;
    }
  }

  const finalSaturation = (lowSaturation + highSaturation) / 2;

  return hsvToRgb(hsv.hue, finalSaturation, targetChroma / finalSaturation);
}

function solveFillOpacity(
  props: RGBColor & { targetChroma: number; minOpacity: number; maxOpacity: number },
) {
  const rawChroma =
    (Math.max(props.red, props.green, props.blue) - Math.min(props.red, props.green, props.blue)) /
    255;

  if (rawChroma === 0) {
    return props.maxOpacity;
  }

  return Math.max(props.minOpacity, Math.min(props.maxOpacity, props.targetChroma / rawChroma));
}

function blendOverWhite(props: RGBColor & { opacity: number }): RGBColor {
  return {
    blue: Math.round(props.blue * props.opacity + 255 * (1 - props.opacity)),
    green: Math.round(props.green * props.opacity + 255 * (1 - props.opacity)),
    red: Math.round(props.red * props.opacity + 255 * (1 - props.opacity)),
  };
}

function darkenToTargetLuminance(props: RGBColor & { targetLuminance: number }): RGBColor {
  const hsv = rgbToHsv(props.red, props.green, props.blue);
  let lowValue = 0;
  let highValue = hsv.value;

  for (let index = 0; index < 12; index++) {
    const midValue = (lowValue + highValue) / 2;
    const color = hsvToRgb(hsv.hue, hsv.saturation, midValue);

    if (getPerceivedLuminance(color.red, color.green, color.blue) < props.targetLuminance) {
      lowValue = midValue;
    } else {
      highValue = midValue;
    }
  }

  return hsvToRgb(hsv.hue, hsv.saturation, (lowValue + highValue) / 2);
}

function darkenByLuminanceRatio(props: RGBColor & { ratio: number }): RGBColor {
  const currentLuminance = getPerceivedLuminance(props.red, props.green, props.blue);

  return darkenToTargetLuminance({
    blue: props.blue,
    green: props.green,
    red: props.red,
    targetLuminance: currentLuminance * props.ratio,
  });
}

function adjustLuminanceTo(props: RGBColor & { targetLuminance: number }): RGBColor {
  const linearRed = srgbToLinear(props.red);
  const linearGreen = srgbToLinear(props.green);
  const linearBlue = srgbToLinear(props.blue);
  const currentLuminance =
    LUMINANCE_RED * linearRed + LUMINANCE_GREEN * linearGreen + LUMINANCE_BLUE * linearBlue;
  const scale = props.targetLuminance / currentLuminance;

  return {
    blue: linearToSrgb(linearBlue * scale),
    green: linearToSrgb(linearGreen * scale),
    red: linearToSrgb(linearRed * scale),
  };
}

function mixToTargetLuminance(props: RGBColor & { targetLuminance: number }): RGBColor {
  const linearRed = srgbToLinear(props.red);
  const linearGreen = srgbToLinear(props.green);
  const linearBlue = srgbToLinear(props.blue);
  const currentLuminance =
    LUMINANCE_RED * linearRed + LUMINANCE_GREEN * linearGreen + LUMINANCE_BLUE * linearBlue;

  if (currentLuminance >= props.targetLuminance || currentLuminance >= 1) {
    return { blue: props.blue, green: props.green, red: props.red };
  }

  const amount = (props.targetLuminance - currentLuminance) / (1 - currentLuminance);

  return {
    blue: linearToSrgb(linearBlue + amount * (1 - linearBlue)),
    green: linearToSrgb(linearGreen + amount * (1 - linearGreen)),
    red: linearToSrgb(linearRed + amount * (1 - linearRed)),
  };
}

function applyContrastTransform(props: RGBColor & { lightnessDelta: number }): RGBColor {
  const hsl = rgbToHsl(props.red, props.green, props.blue);
  const boostedSaturation = Math.min(1, hsl.saturation * SHARED_MODE.pressInSaturationBoost);
  const targetLightness = Math.max(0, Math.min(1, hsl.lightness + props.lightnessDelta));

  return hslToRgb(hsl.hue, boostedSaturation, targetLightness);
}

function getPerceivedLuminance(red: number, green: number, blue: number) {
  return (
    LUMINANCE_RED * srgbToLinear(red) +
    LUMINANCE_GREEN * srgbToLinear(green) +
    LUMINANCE_BLUE * srgbToLinear(blue)
  );
}

function rgbToHsl(red: number, green: number, blue: number) {
  const normalizedRed = red / 255;
  const normalizedGreen = green / 255;
  const normalizedBlue = blue / 255;
  const max = Math.max(normalizedRed, normalizedGreen, normalizedBlue);
  const min = Math.min(normalizedRed, normalizedGreen, normalizedBlue);
  const lightness = (max + min) / 2;

  if (max === min) {
    return { hue: 0, lightness, saturation: 0 };
  }

  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let hue: number;

  if (max === normalizedRed) {
    hue = (normalizedGreen - normalizedBlue) / delta + (normalizedGreen < normalizedBlue ? 6 : 0);
  } else if (max === normalizedGreen) {
    hue = (normalizedBlue - normalizedRed) / delta + 2;
  } else {
    hue = (normalizedRed - normalizedGreen) / delta + 4;
  }

  return { hue: hue / 6, lightness, saturation };
}

function hslToRgb(hue: number, saturation: number, lightness: number): RGBColor {
  if (saturation === 0) {
    const value = Math.round(lightness * 255);

    return { blue: value, green: value, red: value };
  }

  const q =
    lightness < 0.5
      ? lightness * (1 + saturation)
      : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;

  return {
    blue: Math.round(hueToRgbComponent(p, q, hue - 1 / 3) * 255),
    green: Math.round(hueToRgbComponent(p, q, hue) * 255),
    red: Math.round(hueToRgbComponent(p, q, hue + 1 / 3) * 255),
  };
}

function hueToRgbComponent(p: number, q: number, t: number) {
  let normalizedT = t;

  if (normalizedT < 0) {
    normalizedT += 1;
  }

  if (normalizedT > 1) {
    normalizedT -= 1;
  }

  if (normalizedT < 1 / 6) {
    return p + (q - p) * 6 * normalizedT;
  }

  if (normalizedT < 1 / 2) {
    return q;
  }

  if (normalizedT < 2 / 3) {
    return p + (q - p) * (2 / 3 - normalizedT) * 6;
  }

  return p;
}

function rgbToHsv(red: number, green: number, blue: number) {
  const normalizedRed = red / 255;
  const normalizedGreen = green / 255;
  const normalizedBlue = blue / 255;
  const max = Math.max(normalizedRed, normalizedGreen, normalizedBlue);
  const min = Math.min(normalizedRed, normalizedGreen, normalizedBlue);
  const delta = max - min;
  const value = max;
  const saturation = max === 0 ? 0 : delta / max;

  if (delta === 0) {
    return { hue: 0, saturation, value };
  }

  let hue: number;

  if (max === normalizedRed) {
    hue = (normalizedGreen - normalizedBlue) / delta + (normalizedGreen < normalizedBlue ? 6 : 0);
  } else if (max === normalizedGreen) {
    hue = (normalizedBlue - normalizedRed) / delta + 2;
  } else {
    hue = (normalizedRed - normalizedGreen) / delta + 4;
  }

  return { hue: hue / 6, saturation, value };
}

function hsvToRgb(hue: number, saturation: number, value: number): RGBColor {
  const chroma = value * saturation;
  const huePrime = (hue * 6) % 6;
  const secondLargestComponent = chroma * (1 - Math.abs((huePrime % 2) - 1));
  const match = value - chroma;

  let redComponent: number;
  let greenComponent: number;
  let blueComponent: number;

  if (huePrime < 1) {
    redComponent = chroma;
    greenComponent = secondLargestComponent;
    blueComponent = 0;
  } else if (huePrime < 2) {
    redComponent = secondLargestComponent;
    greenComponent = chroma;
    blueComponent = 0;
  } else if (huePrime < 3) {
    redComponent = 0;
    greenComponent = chroma;
    blueComponent = secondLargestComponent;
  } else if (huePrime < 4) {
    redComponent = 0;
    greenComponent = secondLargestComponent;
    blueComponent = chroma;
  } else if (huePrime < 5) {
    redComponent = secondLargestComponent;
    greenComponent = 0;
    blueComponent = chroma;
  } else {
    redComponent = chroma;
    greenComponent = 0;
    blueComponent = secondLargestComponent;
  }

  return {
    blue: Math.round((blueComponent + match) * 255),
    green: Math.round((greenComponent + match) * 255),
    red: Math.round((redComponent + match) * 255),
  };
}

function srgbToLinear(value: number) {
  const normalized = value / 255;

  if (normalized <= SRGB_LINEAR_THRESHOLD) {
    return normalized / SRGB_LINEAR_SCALE;
  }

  return Math.pow((normalized + 0.055) / 1.055, SRGB_GAMMA);
}

function linearToSrgb(value: number) {
  if (value <= SRGB_INVERSE_THRESHOLD) {
    return Math.round(SRGB_LINEAR_SCALE * value * 255);
  }

  return Math.round((1.055 * Math.pow(value, 1 / SRGB_GAMMA) - 0.055) * 255);
}

function getRGBAFromRGB(red: number, green: number, blue: number, alpha: number) {
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
